import { api } from "@/utils/api";
import { FormEvent, useState, useRef, useEffect } from "react";
import { toast } from "@/utils/appToast";

type PhoneVerificationFormProps = {
  phone: string;
  onHide: () => void;
  onVerificationSuccess: (phoneCode: string) => void;
};

const COOLDOWN_KEY = "phone_otp_cooldown";
const COOLDOWN_DURATION = 300; // 5 minutes

export const PhoneVerification = ({ phone, onHide, onVerificationSuccess }: PhoneVerificationFormProps) => {
  const [cooldown, setCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Load cooldown from localStorage
  useEffect(() => {
    const storedExpiry = localStorage.getItem(COOLDOWN_KEY);
    if (storedExpiry) {
      const expiry = parseInt(storedExpiry, 10);
      const remaining = expiry - Math.floor(Date.now() / 1000);
      if (remaining > 0) {
        setCooldown(remaining);
      } else {
        localStorage.removeItem(COOLDOWN_KEY);
      }
    } else {
      startCooldown(); // on initial mount
    }
  }, []);

  // Start cooldown and persist in localStorage
  const startCooldown = () => {
    const expiry = Math.floor(Date.now() / 1000) + COOLDOWN_DURATION;
    localStorage.setItem(COOLDOWN_KEY, expiry.toString());
    setCooldown(COOLDOWN_DURATION);
  };

  // Countdown effect
  useEffect(() => {
    if (cooldown === 0) return;
    const interval = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          localStorage.removeItem(COOLDOWN_KEY);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleResendOTP = async () => {
    try {
      setResendLoading(true);
      await api.forgotPassword({ type: "sms", phone });
      toast.success("OTP reenviado con éxito");
      startCooldown();
    } catch (error) {
      toast.error("No se pudo reenviar el código. Inténtelo de nuevo.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(0, 1);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const verificationCode = code.join('');
      const data = await api.forgotPasswordByPhone({ otp: verificationCode, phone });

      if (data.success) {
        toast.success("Código verificado con éxito");
        onVerificationSuccess(verificationCode);
      } else {
        toast.error(data.message || "Verificación fallida. Inténtelo de nuevo.");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Ocurrió un error inesperado.";
      toast.error(`Error: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="default-form">
      <div className="form-group text-center mb-4">
        <h1 className="text-white" style={{ fontSize: '20px' }}>Enter OTP</h1>
        <p className="mt-3 text-white">We have sent a OTP code on <br /> {phone}</p>
      </div>
      <div className="form-group">
        <div className="d-flex justify-content-center gap-2 mb-4">
          {Array(6).fill(0).map((_, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              maxLength={1}
              value={code[index]}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="form-control otp-input"
              style={{
                width: 'clamp(32px, 10vw, 55px)',
                height: 'clamp(32px, 10vw, 55px)',
                textAlign: 'center',
                fontSize: 'clamp(16px, 5vw, 28px)',
                borderRadius: 0,
                boxSizing: 'border-box',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                outline: 'none',
                border: '1px solid #ced4da',
                background: '#fff',
                marginRight: '4px',
                padding: 0,
                lineHeight: 1,
                verticalAlign: 'middle',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          ))}
        </div>
      </div>
      <div className="form-group">
        <button
          className="theme-btn btn-style-one w-100"
          type="submit"
          disabled={isLoading || code.some(digit => !digit)}
          style={{ backgroundColor: "#1967d2", border: "none", height: '50px', fontSize: '16px' }}
        >
          {isLoading ? <div className="spinner-border spinner-border-sm" role="status" /> : "Enviar"}
        </button>
      </div>
      <div className="text-center text-white mt-3">
        <p>¿No ha recibido el código?</p>
        <button
          type="button"
          onClick={handleResendOTP}
          disabled={cooldown > 0 || resendLoading}
          className="btn btn-link text-decoration-underline"
          style={{
            color: "#fff",
            fontSize: "16px",
            padding: 0,
            opacity: cooldown > 0 ? 0.5 : 1,
            cursor: cooldown > 0 ? "not-allowed" : "pointer"
          }}
        >
          {cooldown > 0
            ? `Vuelva a intentarlo en ${Math.floor(cooldown / 60)}:${String(cooldown % 60).padStart(2, "0")}`
            : resendLoading
              ? "Reenviando..."
              : "Inténtalo de nuevo"}
        </button>
      </div>
    </form>
  );
};
